import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, ShieldCheck, ShieldAlert, Building2, ScrollText,
  QrCode, Bell, Settings, LogOut, Sun, Moon, Search, Sparkles, LifeBuoy, Terminal, TrendingUp, Activity, Briefcase, Mail, Handshake,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { VLKButton, VLKUserAvatar, VLKPermissionBanner } from "@/components/vlk";
import { IDS } from "@/constants/testIds";
import { useRealtimeNotifications } from "@/hooks/useRealtimeNotifications";
import LiveOpsConsole from "@/components/liveAudit/LiveOpsConsole";

const NAV = [
  { to: "/dashboard",   key: "dashboard",  label: "Dashboard",    icon: LayoutDashboard },
  { to: "/analytics",   key: "analytics",  label: "Analytics",    icon: TrendingUp },
  { to: "/digest",      key: "digest",     label: "Digest",       icon: Mail },
  { to: "/email",       key: "email",      label: "Email Ops",    icon: Mail },
  { to: "/security",    key: "security",   label: "Security",     icon: ShieldCheck },
  { to: "/identities",  key: "identities", label: "Identities",   icon: ShieldCheck },
  { to: "/approvals",   key: "approvals",  label: "Approvals",    icon: ShieldAlert },
  { to: "/recovery",    key: "recovery",   label: "Recovery",     icon: LifeBuoy },
  { to: "/cases",       key: "cases",      label: "Cases",        icon: Briefcase },
  { to: "/institutions",key: "institutions",label: "Institutions",icon: Building2 },
  { to: "/onboarding",  key: "onboarding",  label: "Onboarding",   icon: Handshake },
  { to: "/audit",       key: "audit",      label: "Audit Ledger", icon: ScrollText },
  { to: "/audit/live",  key: "audit-live", label: "Live Audit",   icon: Activity },
  { to: "/qr",          key: "qr",         label: "QR Verify",    icon: QrCode },
  { to: "/notifications",key:"notifications",label:"Notifications",icon: Bell },
  { to: "/developer",   key: "developer",  label: "Developer",    icon: Terminal },
  { to: "/users",       key: "users",      label: "Users & Roles",icon: Users },
  { to: "/design-system",key:"design",     label: "Design System",icon: Sparkles },
  { to: "/settings",    key: "settings",   label: "Settings",     icon: Settings },
];

function Sidebar() {
  return (
    <aside data-testid={IDS.sidebar} className="w-60 shrink-0 border-r border-border bg-card flex flex-col">
      <div className="px-5 h-14 flex items-center gap-2 border-b border-border">
        <div className="h-7 w-7 bg-foreground text-background flex items-center justify-center rounded-sm">
          <ShieldCheck className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <div className="font-display text-sm tracking-tight leading-none">VeraLok</div>
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">Trust Verification Platform</div>
        </div>
      </div>
      <nav className="flex-1 p-2 flex flex-col gap-0.5">
        {NAV.map((item) => (
          <NavLink
            key={item.key}
            to={item.to}
            data-testid={IDS.sidebarNav(item.key)}
            className={({ isActive }) => cn(
              "flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm transition-colors",
              isActive
                ? "bg-secondary text-foreground font-medium"
                : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
            )}
          >
            <item.icon className="h-4 w-4" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border text-[10px] uppercase tracking-widest text-muted-foreground font-mono">
        v1.0.0 · secure
      </div>
    </aside>
  );
}

function Topbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const { unread } = useRealtimeNotifications();
  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <header className="h-14 border-b border-border bg-card flex items-center px-6 gap-4">
      <div className="flex-1 max-w-md relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          data-testid={IDS.globalSearch}
          placeholder="Search VLK_ID, institution, audit event…"
          className="w-full h-9 pl-9 pr-3 rounded-sm bg-background border border-border text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        />
      </div>
      {user && <VLKPermissionBanner role={user.role} />}
      <button
        data-testid="topbar-notifications"
        onClick={() => navigate("/notifications")}
        className="relative h-9 w-9 flex items-center justify-center rounded-sm border border-border hover:bg-secondary transition-colors"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span
            data-testid="topbar-notifications-badge"
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[9px] font-mono flex items-center justify-center tabular"
          >{unread > 99 ? "99+" : unread}</span>
        )}
      </button>
      <button
        data-testid={IDS.themeToggle}
        onClick={toggle}
        className="h-9 w-9 flex items-center justify-center rounded-sm border border-border hover:bg-secondary transition-colors"
        aria-label="Toggle theme"
      >
        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>
      <div className="flex items-center gap-3 pl-4 border-l border-border">
        <div className="text-right hidden md:block">
          <div className="text-sm font-medium leading-none">{user?.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1 font-mono">{user?.role}</div>
        </div>
        <VLKUserAvatar name={user?.name} src={user?.avatar_url} size={32} testId={IDS.userAvatar} />
        <VLKButton data-testid={IDS.logoutBtn} variant="ghost" size="icon" onClick={doLogout} aria-label="Log out">
          <LogOut className="h-4 w-4" />
        </VLKButton>
      </div>
    </header>
  );
}

export default function AppShell({ children }) {
  return (
    <div className="min-h-screen flex bg-background">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Topbar />
        <main className="flex-1 overflow-auto">
          <div className="p-8 max-w-[1600px]">
            {children}
          </div>
        </main>
      </div>
      <LiveOpsConsole />
    </div>
  );
}
